export const INCOMPLETE_ROLE_LABEL = "Customer";

const USER_TYPE_BACKEND_TO_DISPLAY_MAP = {
  accountant: "Bookkeeper",
  admin: "Admin",
  client: "Customer",
  external_accountant: "Accountant",
  lead: "Lead",
  manager: "Manager",
  viewer: "Viewer",
};

const INTERNAL_BACKEND_TYPES = ["admin", "manager", "accountant"];
const EXTERNAL_BACKEND_TYPES = [
  "external_accountant",
  "client",
  "lead",
  "viewer",
];

const ROLE_CONSTRAINTS = {
  Admin: ["Manager", "Bookkeeper"],
  Manager: ["Manager", "Bookkeeper"],
  Bookkeeper: ["Bookkeeper"],
  Lead: ["Lead", "Viewer"],
  Customer: ["Customer", "Viewer"],
  Viewer: ["Viewer"],
  Accountant: ["Accountant", "Viewer"],
};

const ROLE_BACKEND_OVERRIDES = {
  Accountant: "External Accountant",
};

const ROLE_INPUT_TO_DISPLAY_MAP = {
  admin: "Admin",
  manager: "Manager",
  bookkeeper: "Bookkeeper",
  accountant: "Accountant",
  external_accountant: "Accountant",
  lead: "Lead",
  customer: "Customer",
  client: "Customer",
  viewer: "Viewer",
};

export const getAllowedAccountRoles = (userType) => {
  if (!userType) return [];
  
  // Normalize the user type to match ROLE_CONSTRAINTS keys (capitalize)
  const normalizedUserType = normalizeAccountRoleLabel(userType) || userType;
  return ROLE_CONSTRAINTS[normalizedUserType] || [];
};

export const normalizeAccountRoleLabel = (role) => {
  if (!role) {
    return null;
  }

  const backendFromDisplay = mapUserTypeDisplayToBackend(role);
  if (backendFromDisplay && USER_TYPE_BACKEND_TO_DISPLAY_MAP[backendFromDisplay]) {
    return USER_TYPE_BACKEND_TO_DISPLAY_MAP[backendFromDisplay];
  }

  const directKey = role.toString().trim().toLowerCase();
  if (USER_TYPE_BACKEND_TO_DISPLAY_MAP[directKey]) {
    return USER_TYPE_BACKEND_TO_DISPLAY_MAP[directKey];
  }

  const cleaned = role.toString().trim();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : null;
};

export const isRoleAllowedForUserType = (role, userType) =>
  getAllowedAccountRoles(userType).includes(role);

export const mapRoleLabelToBackend = (role) => ROLE_BACKEND_OVERRIDES[role] || role;

const normalizeBackendUserType = (type) => {
  if (!type) {
    return null;
  }

  return type.toString().trim().toLowerCase();
};

const normalizeDisplayUserType = (displayType) => {
  if (!displayType) {
    return null;
  }

  return displayType.toString().trim().toLowerCase();
};

export const mapUserTypeBackendToDisplay = (type) => {
  const normalized = normalizeBackendUserType(type);

  if (!normalized) {
    return "";
  }

  if (USER_TYPE_BACKEND_TO_DISPLAY_MAP[normalized]) {
    return USER_TYPE_BACKEND_TO_DISPLAY_MAP[normalized];
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const USER_TYPE_DISPLAY_TO_BACKEND_MAP = (() => {
  const map = {};

  Object.entries(USER_TYPE_BACKEND_TO_DISPLAY_MAP).forEach(
    ([backend, display]) => {
      map[display.toLowerCase()] = backend;
    }
  );

  map.administrator = "admin";

  return map;
})();

export const mapUserTypeDisplayToBackend = (displayType) => {
  const normalized = normalizeDisplayUserType(displayType);

  if (!normalized) {
    return null;
  }

  if (USER_TYPE_DISPLAY_TO_BACKEND_MAP[normalized]) {
    return USER_TYPE_DISPLAY_TO_BACKEND_MAP[normalized];
  }

  return normalized.replace(/\s+/g, "_");
};

const INTERNAL_USER_TYPES_DISPLAY = INTERNAL_BACKEND_TYPES.map(
  mapUserTypeBackendToDisplay
);
const EXTERNAL_USER_TYPES_DISPLAY = EXTERNAL_BACKEND_TYPES.map(
  mapUserTypeBackendToDisplay
);

export const getInternalUserTypesDisplay = () => [
  ...new Set(INTERNAL_USER_TYPES_DISPLAY),
];

export const getExternalUserTypesDisplay = () => [
  ...new Set(EXTERNAL_USER_TYPES_DISPLAY),
];

export const isInternalUserTypeBackend = (type) => {
  const normalized = normalizeBackendUserType(type);
  return normalized ? INTERNAL_BACKEND_TYPES.includes(normalized) : false;
};

export const isExternalUserTypeBackend = (type) => {
  const normalized = normalizeBackendUserType(type);
  return normalized ? EXTERNAL_BACKEND_TYPES.includes(normalized) : false;
};

export const getUserCategoryForBackendType = (type) => {
  if (isInternalUserTypeBackend(type)) {
    return "Internal";
  }

  if (isExternalUserTypeBackend(type)) {
    return "External";
  }

  return null;
};

export const isInternalUserTypeDisplay = (displayType) => {
  const backend = mapUserTypeDisplayToBackend(displayType);
  return backend ? isInternalUserTypeBackend(backend) : false;
};

export const getExistingRoleHolder = (account, selectedRole) => {
  if (!account || !selectedRole) {
    return null;
  }

  if (selectedRole === "Bookkeeper" || selectedRole === "Accountant") {
    return account.bookkeeper || null;
  }

  if (selectedRole === "Manager") {
    return account.manager || null;
  }

  return null;
};
